import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserData, getAllUsers, getUsersByRole, createUser, updateUser, deleteUser } from "../services/userService";

export function useUsers(role?: string) {
  const queryClient = useQueryClient();

  // If role is provided, use role-based API, otherwise use getAll API
  const usersQuery = useQuery<UserData[], Error>({
    queryKey: role ? ["users", role] : ["users"],
    queryFn: role ? () => getUsersByRole(role) : getAllUsers,
    staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh for 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes - keep in cache
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Use cached data instead of refetching on every mount
  });

  const createUserMutation = useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["schoolAdmins"] });
      // Also invalidate role-based queries if role was provided
      if (role) {
        queryClient.invalidateQueries({ queryKey: ["users", role] });
      }
    },
  });

  const updateUserMutation = useMutation(
    (args: { id: number; data: UserData }) => updateUser(args.id, args.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["users"] });
        queryClient.invalidateQueries({ queryKey: ["schoolAdmins"] });
        // Also invalidate role-based queries if role was provided
        if (role) {
          queryClient.invalidateQueries({ queryKey: ["users", role] });
        }
      },
    }
  );

  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["schoolAdmins"] });
      // Also invalidate role-based queries if role was provided
      if (role) {
        queryClient.invalidateQueries({ queryKey: ["users", role] });
      }
    },
  });

  return {
    usersQuery,
    createUserMutation,
    updateUserMutation,
    deleteUserMutation,
  };
}
